import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Input, Spin, Empty } from "antd";
import { Virtuoso } from "react-virtuoso";
import { Icon } from "Components/UI";
import Button from "Components/UI/Button";
import { ActivitiesActions } from "Store/Activities/Activities.actions";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";
import { ITopicDnDResponse } from "Data/interfaces/Activities/IDragAndDropDataFormat";
import ActivityCard from "./ActivityCard";
import styles from "./ActivitiesList.module.scss";

// Fora do componente de propósito: uma função nova a cada render faz o
// Virtuoso desmontar e remontar o rodapé.
const ListFooter = () => <div className={styles.listFooter} />;

interface IActivitiesList {
  constructionSiteId: number;
  onCreateActivity: () => void;
  onOpenActivity: (activityId: string) => void;
}

const ActivitiesList: FC<IActivitiesList> = ({
  constructionSiteId,
  onCreateActivity,
  onOpenActivity,
}) => {
  const { t } = useTranslation('federatedViewer');
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");

  const { listActivities, isLoading } = useSelector((state: IGlobalReducerState) => ({
    listActivities: state.activities.listActivities,
    isLoading: state.activities.isLoading,
  }));

  useEffect(() => {
    // Load activities when component mounts
    dispatch(ActivitiesActions.listActivities({
      csId: constructionSiteId,
    }));
  }, [dispatch, constructionSiteId]);

  const activities = useMemo(() => {
    if (!listActivities?.activities) {
      return [];
    }

    // Convert activities object to array
    return Object.values(listActivities.activities) as ITopicDnDResponse[];
  }, [listActivities]);

  const filteredActivities = useMemo(() => {
    if (!searchTerm) {
      return activities;
    }

    const searchLower = searchTerm.toLowerCase();
    return activities.filter((activity) =>
      activity.Title.toLowerCase().includes(searchLower) ||
      activity.Identifier.toLowerCase().includes(searchLower) ||
      activity.Description?.toLowerCase().includes(searchLower)
    );
  }, [activities, searchTerm]);

  const handleRefresh = () => {
    dispatch(ActivitiesActions.listActivities({
      csId: constructionSiteId,
    }));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h3 className={styles.title}>{t('activitiesList.title')}</h3>
          <div className={styles.actions}>
            <button
              className={styles.refreshButton}
              onClick={handleRefresh}
              title={t('activitiesList.refresh')}
            >
              <Icon icon="atualizar" customSize={16} />
            </button>
          </div>
        </div>

        <Button
          type="primary"
          width="100%"
          height={36}
          onClick={onCreateActivity}
          className={styles.createButton}
        >
          <Icon icon="adicionarAlt" customSize={16} color="branco" />
          <span>{t('activitiesList.newActivity')}</span>
        </Button>

        <div className={styles.searchContainer}>
          <Input
            placeholder={t('activitiesList.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<Icon icon="buscar" customSize={14} />}
            allowClear
          />
        </div>
      </div>

      <div className={styles.listContainer}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <Empty
            description={
              searchTerm
                ? t('activitiesList.emptySearch')
                : t('activitiesList.emptyList')
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Virtuoso
            style={{ height: '100%' }}
            totalCount={filteredActivities.length}
            data={filteredActivities}
            computeItemKey={(_, activity) => activity.Guid}
            components={{ Footer: ListFooter }}
            itemContent={(_, activity) => (
              <div className={styles.row}>
                <ActivityCard
                  activity={activity}
                  searchTerm={searchTerm}
                  onClick={() => onOpenActivity(activity.Guid)}
                />
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default ActivitiesList;
